import React, { useState, useEffect, useCallback } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Radio, Plus, Trash2, RefreshCw, Search, TrendingUp, TrendingDown, Minus,
  MessageCircle, Heart, Share2, ExternalLink, X, Sparkles, Eye, ChevronRight,
  BarChart3, Globe, Filter
} from 'lucide-react';
import { api, isUpgradeError } from '../context/AuthContext';
import { toast } from 'sonner';

const PLATFORM_CONFIG = {
  twitter: { label: 'Twitter/X', color: 'bg-sky-500', textColor: 'text-sky-700', bgLight: 'bg-sky-50' },
  instagram: { label: 'Instagram', color: 'bg-pink-500', textColor: 'text-pink-700', bgLight: 'bg-pink-50' },
  facebook: { label: 'Facebook', color: 'bg-blue-600', textColor: 'text-blue-700', bgLight: 'bg-blue-50' },
  linkedin: { label: 'LinkedIn', color: 'bg-blue-700', textColor: 'text-blue-700', bgLight: 'bg-blue-50' },
  tiktok: { label: 'TikTok', color: 'bg-slate-800', textColor: 'text-slate-700', bgLight: 'bg-slate-50' },
  youtube: { label: 'YouTube', color: 'bg-red-600', textColor: 'text-red-700', bgLight: 'bg-red-50' },
};

const SENTIMENT_CONFIG = {
  positive: { icon: TrendingUp, color: 'text-emerald-600', bg: 'bg-emerald-50', border: 'border-emerald-200', label: 'Positive' },
  neutral: { icon: Minus, color: 'text-slate-500', bg: 'bg-slate-50', border: 'border-slate-200', label: 'Neutral' },
  negative: { icon: TrendingDown, color: 'text-red-500', bg: 'bg-red-50', border: 'border-red-200', label: 'Negative' },
};

// ========== Stat Card ==========
const StatCard = ({ title, value, icon: Icon, color, subtitle }) => (
  <Card data-testid={`stat-${title.toLowerCase().replace(/\s/g, '-')}`}>
    <CardContent className="p-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">{title}</p>
          <p className="text-2xl font-bold text-slate-900 mt-1">{value}</p>
          {subtitle && <p className="text-xs text-slate-400 mt-0.5">{subtitle}</p>}
        </div>
        <div className={`w-10 h-10 rounded-xl ${color} flex items-center justify-center`}>
          <Icon className="h-5 w-5 text-white" />
        </div>
      </div>
    </CardContent>
  </Card>
);

// ========== Mention Card ==========
const MentionCard = ({ mention }) => {
  const sentimentCfg = SENTIMENT_CONFIG[mention.sentiment] || SENTIMENT_CONFIG.neutral;
  const SentimentIcon = sentimentCfg.icon;
  const platCfg = PLATFORM_CONFIG[mention.platform] || { label: mention.platform, color: 'bg-slate-500', textColor: 'text-slate-700', bgLight: 'bg-slate-50' };

  return (
    <div className="p-4 border border-slate-200 rounded-xl hover:border-slate-300 transition-colors" data-testid="mention-card">
      <div className="flex items-start gap-3">
        <img src={mention.author_avatar} alt="" className="w-9 h-9 rounded-full bg-slate-100 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold text-slate-800">{mention.author_name}</span>
            <span className="text-xs text-slate-400">{mention.author_handle}</span>
            <Badge variant="outline" className={`text-[10px] px-1.5 py-0 ${platCfg.bgLight} ${platCfg.textColor} border-transparent`}>
              {platCfg.label}
            </Badge>
            <div className={`flex items-center gap-0.5 ${sentimentCfg.color}`}>
              <SentimentIcon className="h-3 w-3" />
              <span className="text-[10px] font-medium">{sentimentCfg.label}</span>
            </div>
          </div>
          <p className="text-sm text-slate-600 mt-1.5 leading-relaxed">{mention.content}</p>
          <div className="flex items-center gap-4 mt-2 text-xs text-slate-400">
            <span className="flex items-center gap-1"><Heart className="h-3 w-3" />{mention.engagement?.likes || 0}</span>
            <span className="flex items-center gap-1"><MessageCircle className="h-3 w-3" />{mention.engagement?.comments || 0}</span>
            <span className="flex items-center gap-1"><Share2 className="h-3 w-3" />{mention.engagement?.shares || 0}</span>
            <span>{new Date(mention.detected_at).toLocaleDateString()}</span>
            {mention.source_url && (
              <a
                href={mention.source_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-emerald-600 hover:text-emerald-700 flex items-center gap-0.5"
                onClick={(e) => {
                  if (mention.source_url.includes('example.com') || mention.source_url.startsWith('#')) {
                    e.preventDefault();
                    return;
                  }
                }}
              >
                <ExternalLink className="h-3 w-3" />
                {mention.source_url.includes('example.com') || mention.source_url.startsWith('#') ? 'Demo Link' : 'View Source'}
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ========== Create Topic Dialog ==========
const CreateTopicDialog = ({ open, onClose, onCreated }) => {
  const [name, setName] = useState('');
  const [keywordsInput, setKeywordsInput] = useState('');
  const [platforms, setPlatforms] = useState(['twitter', 'instagram', 'facebook']);
  const [loading, setLoading] = useState(false);

  const handleCreate = async () => {
    if (!name.trim() || !keywordsInput.trim()) {
      toast.error('Please fill in the topic name and at least one keyword');
      return;
    }
    setLoading(true);
    try {
      const keywords = keywordsInput.split(',').map(k => k.trim()).filter(Boolean);
      const res = await api.post('/listening/topics', { name, keywords, platforms, notify_on_mention: true });
      toast.success(`Topic "${res.data.name}" created with mentions tracked`);
      onCreated(res.data);
      setName(''); setKeywordsInput(''); onClose();
    } catch (e) {
      if (!isUpgradeError(e)) toast.error(e.response?.data?.detail || 'Failed to create topic');
    } finally {
      setLoading(false);
    }
  };

  const togglePlatform = (p) => setPlatforms(prev => prev.includes(p) ? prev.filter(x => x !== p) : [...prev, p]);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg" data-testid="create-topic-dialog">
        <DialogHeader>
          <DialogTitle>Create Listening Topic</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <label className="text-sm font-medium text-slate-700">Topic Name</label>
            <Input value={name} onChange={e => setName(e.target.value)} placeholder="e.g., Brand Mentions, Product Feedback" className="mt-1" data-testid="topic-name-input" />
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700">Keywords (comma separated)</label>
            <Input value={keywordsInput} onChange={e => setKeywordsInput(e.target.value)} placeholder="e.g., StelliSocial, #stellisocial, social media tool" className="mt-1" data-testid="topic-keywords-input" />
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700 mb-2 block">Platforms to Monitor</label>
            <div className="flex flex-wrap gap-2">
              {Object.entries(PLATFORM_CONFIG).map(([key, cfg]) => (
                <button key={key} onClick={() => togglePlatform(key)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${platforms.includes(key) ? `${cfg.color} text-white` : 'bg-slate-100 text-slate-500'}`}
                  data-testid={`platform-toggle-${key}`}
                >{cfg.label}</button>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleCreate} disabled={loading} data-testid="create-topic-btn">
            {loading ? 'Creating...' : 'Create Topic'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ========== Main Page ==========
export default function SocialListeningPage() {
  const [topics, setTopics] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [mentions, setMentions] = useState([]);
  const [mentionsTotal, setMentionsTotal] = useState(0);
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [sentimentFilter, setSentimentFilter] = useState('all');
  const [platformFilter, setPlatformFilter] = useState('all');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [analyzingAi, setAnalyzingAi] = useState(false);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('dashboard');

  const fetchData = useCallback(async () => {
    try {
      const [topicsRes, dashRes] = await Promise.all([
        api.get('/listening/topics'),
        api.get('/listening/dashboard'),
      ]);
      setTopics(topicsRes.data);
      setDashboard(dashRes.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchMentions = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (selectedTopic) params.append('topic_id', selectedTopic);
      if (sentimentFilter !== 'all') params.append('sentiment', sentimentFilter);
      if (platformFilter !== 'all') params.append('platform', platformFilter);
      params.append('limit', '50');
      const res = await api.get(`/listening/mentions?${params}`);
      setMentions(res.data.mentions);
      setMentionsTotal(res.data.total);
    } catch (e) {
      console.error(e);
    }
  }, [selectedTopic, sentimentFilter, platformFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { fetchMentions(); }, [fetchMentions]);

  const handleDeleteTopic = async (topicId) => {
    if (!window.confirm('Delete this topic and all its mentions?')) return;
    try {
      await api.delete(`/listening/topics/${topicId}`);
      toast.success('Topic deleted');
      setTopics(prev => prev.filter(t => t.topic_id !== topicId));
      if (selectedTopic === topicId) setSelectedTopic(null);
      fetchData();
    } catch (e) {
      toast.error('Failed to delete topic');
    }
  };

  const handleRefreshTopic = async (topicId) => {
    try {
      await api.post(`/listening/topics/${topicId}/refresh`);
      toast.success('New mentions generated');
      fetchMentions();
      fetchData();
    } catch (e) {
      toast.error('Failed to refresh mentions');
    }
  };

  const handleAiAnalysis = async () => {
    setAnalyzingAi(true);
    try {
      const res = await api.post('/listening/analyze-sentiment');
      setAiAnalysis(res.data.analysis);
    } catch (e) {
      if (!isUpgradeError(e)) toast.error('Failed to generate analysis');
    } finally {
      setAnalyzingAi(false);
    }
  };

  const sentimentData = dashboard?.sentiment || {};
  const totalSentiment = Object.values(sentimentData).reduce((a, b) => a + b, 0) || 1;

  return (
    <DashboardLayout>
      <div className="p-6 max-w-[1400px] mx-auto space-y-6" data-testid="social-listening-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Social Listening</h1>
            <p className="text-sm text-slate-500 mt-1">Monitor brand mentions, keywords, and sentiment across platforms</p>
          </div>
          <Button onClick={() => setShowCreateDialog(true)} data-testid="add-topic-btn">
            <Plus className="h-4 w-4 mr-2" />New Topic
          </Button>
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="dashboard" data-testid="tab-dashboard">Dashboard</TabsTrigger>
            <TabsTrigger value="mentions" data-testid="tab-mentions">Mentions</TabsTrigger>
            <TabsTrigger value="topics" data-testid="tab-topics">Topics</TabsTrigger>
          </TabsList>

          {/* Dashboard Tab */}
          <TabsContent value="dashboard" className="space-y-6 mt-4">
            {loading ? (
              <div className="text-center py-12 text-slate-400">Loading dashboard...</div>
            ) : (
              <>
                {/* Stats */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <StatCard title="Active Topics" value={dashboard?.total_topics || 0} icon={Radio} color="bg-violet-500" />
                  <StatCard title="Total Mentions" value={dashboard?.total_mentions || 0} icon={MessageCircle} color="bg-blue-500" />
                  <StatCard title="This Week" value={dashboard?.recent_mentions_7d || 0} icon={TrendingUp} color="bg-emerald-500" subtitle="Last 7 days" />
                  <StatCard title="Top Keywords" value={dashboard?.top_keywords?.length || 0} icon={Search} color="bg-amber-500" />
                </div>

                {/* Sentiment + Volume */}
                <div className="grid md:grid-cols-2 gap-4">
                  {/* Sentiment Breakdown */}
                  <Card>
                    <CardHeader className="pb-2"><CardTitle className="text-sm">Sentiment Breakdown</CardTitle></CardHeader>
                    <CardContent className="space-y-3">
                      {['positive', 'neutral', 'negative'].map(s => {
                        const count = sentimentData[s] || 0;
                        const pct = Math.round((count / totalSentiment) * 100);
                        const cfg = SENTIMENT_CONFIG[s];
                        return (
                          <div key={s} className="flex items-center gap-3" data-testid={`sentiment-${s}`}>
                            <div className={`w-8 h-8 rounded-lg ${cfg.bg} flex items-center justify-center`}>
                              <cfg.icon className={`h-4 w-4 ${cfg.color}`} />
                            </div>
                            <div className="flex-1">
                              <div className="flex justify-between text-xs mb-1">
                                <span className="font-medium text-slate-700">{cfg.label}</span>
                                <span className="text-slate-500">{count} ({pct}%)</span>
                              </div>
                              <div className="w-full bg-slate-100 rounded-full h-2">
                                <div className={`${cfg.bg} h-2 rounded-full transition-all`} style={{ width: `${pct}%` }} />
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </CardContent>
                  </Card>

                  {/* Volume Trend */}
                  <Card>
                    <CardHeader className="pb-2"><CardTitle className="text-sm">Mention Volume (7 days)</CardTitle></CardHeader>
                    <CardContent>
                      <div className="flex items-end gap-1 h-32">
                        {(dashboard?.volume_trend || []).map((d, i) => {
                          const maxVal = Math.max(...(dashboard?.volume_trend || []).map(v => v.mentions), 1);
                          const height = (d.mentions / maxVal) * 100;
                          return (
                            <div key={d.date} className="flex-1 flex flex-col items-center gap-1">
                              <span className="text-[10px] text-slate-400">{d.mentions}</span>
                              <div className="w-full bg-violet-500 rounded-t" style={{ height: `${Math.max(height, 4)}%` }} />
                              <span className="text-[10px] text-slate-400">{new Date(d.date).toLocaleDateString('en', { weekday: 'short' })}</span>
                            </div>
                          );
                        })}
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Top Keywords + Platform Breakdown */}
                <div className="grid md:grid-cols-2 gap-4">
                  <Card>
                    <CardHeader className="pb-2"><CardTitle className="text-sm">Top Keywords</CardTitle></CardHeader>
                    <CardContent>
                      {(dashboard?.top_keywords || []).length === 0 ? (
                        <p className="text-sm text-slate-400 py-4 text-center">No keywords tracked yet</p>
                      ) : (
                        <div className="space-y-2">
                          {(dashboard?.top_keywords || []).map((kw) => (
                            <div key={kw.keyword} className="flex items-center justify-between py-1">
                              <span className="text-sm text-slate-700 font-medium">{kw.keyword}</span>
                              <Badge variant="outline" className="text-xs">{kw.count} mentions</Badge>
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader className="pb-2"><CardTitle className="text-sm">Platform Distribution</CardTitle></CardHeader>
                    <CardContent>
                      {Object.keys(dashboard?.platforms || {}).length === 0 ? (
                        <p className="text-sm text-slate-400 py-4 text-center">No platform data yet</p>
                      ) : (
                        <div className="space-y-2">
                          {Object.entries(dashboard?.platforms || {}).sort((a, b) => b[1] - a[1]).map(([platform, count]) => {
                            const cfg = PLATFORM_CONFIG[platform] || { label: platform, color: 'bg-slate-500' };
                            const total = Object.values(dashboard?.platforms || {}).reduce((a, b) => a + b, 0) || 1;
                            return (
                              <div key={platform} className="flex items-center gap-3">
                                <div className={`w-3 h-3 rounded-full ${cfg.color}`} />
                                <span className="text-sm text-slate-700 flex-1">{cfg.label}</span>
                                <span className="text-xs text-slate-500">{count}</span>
                                <div className="w-20 bg-slate-100 rounded-full h-1.5">
                                  <div className={`${cfg.color} h-1.5 rounded-full`} style={{ width: `${(count / total) * 100}%` }} />
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>

                {/* AI Analysis */}
                <Card>
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-sm flex items-center gap-2"><Sparkles className="h-4 w-4 text-violet-500" />AI Brand Analysis</CardTitle>
                      <Button size="sm" variant="outline" onClick={handleAiAnalysis} disabled={analyzingAi} data-testid="ai-analysis-btn">
                        {analyzingAi ? 'Analyzing...' : 'Generate Analysis'}
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {aiAnalysis ? (
                      <div className="prose prose-sm max-w-none text-slate-600 whitespace-pre-wrap" data-testid="ai-analysis-result">{aiAnalysis}</div>
                    ) : (
                      <p className="text-sm text-slate-400 py-4 text-center">Click "Generate Analysis" to get AI-powered brand monitoring insights</p>
                    )}
                  </CardContent>
                </Card>
              </>
            )}
          </TabsContent>

          {/* Mentions Tab */}
          <TabsContent value="mentions" className="space-y-4 mt-4">
            <div className="flex items-center gap-3 flex-wrap">
              <Select value={selectedTopic || 'all'} onValueChange={v => setSelectedTopic(v === 'all' ? null : v)}>
                <SelectTrigger className="w-48" data-testid="topic-filter"><SelectValue placeholder="All topics" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Topics</SelectItem>
                  {topics.map(t => <SelectItem key={t.topic_id} value={t.topic_id}>{t.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={sentimentFilter} onValueChange={setSentimentFilter}>
                <SelectTrigger className="w-40" data-testid="sentiment-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Sentiments</SelectItem>
                  <SelectItem value="positive">Positive</SelectItem>
                  <SelectItem value="neutral">Neutral</SelectItem>
                  <SelectItem value="negative">Negative</SelectItem>
                </SelectContent>
              </Select>
              <Select value={platformFilter} onValueChange={setPlatformFilter}>
                <SelectTrigger className="w-40" data-testid="platform-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Platforms</SelectItem>
                  {Object.entries(PLATFORM_CONFIG).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <Badge variant="outline" className="ml-auto">{mentionsTotal} mentions</Badge>
            </div>

            <div className="space-y-3" data-testid="mentions-list">
              {mentions.length === 0 ? (
                <Card><CardContent className="py-12 text-center text-slate-400">No mentions found. Create a topic to start tracking.</CardContent></Card>
              ) : (
                mentions.map(m => <MentionCard key={m.mention_id} mention={m} />)
              )}
            </div>
          </TabsContent>

          {/* Topics Tab */}
          <TabsContent value="topics" className="space-y-4 mt-4">
            {topics.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <Radio className="h-10 w-10 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500 mb-3">No listening topics yet</p>
                  <Button onClick={() => setShowCreateDialog(true)} data-testid="empty-add-topic-btn"><Plus className="h-4 w-4 mr-2" />Create First Topic</Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid md:grid-cols-2 gap-4">
                {topics.map(topic => (
                  <Card key={topic.topic_id} className="hover:border-slate-300 transition-colors" data-testid="topic-card">
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h3 className="font-semibold text-slate-800">{topic.name}</h3>
                          <p className="text-xs text-slate-400 mt-0.5">{topic.mention_count || 0} mentions tracked</p>
                        </div>
                        <div className="flex gap-1">
                          <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => handleRefreshTopic(topic.topic_id)} data-testid="refresh-topic-btn">
                            <RefreshCw className="h-3.5 w-3.5" />
                          </Button>
                          <Button size="icon" variant="ghost" className="h-7 w-7 text-red-500 hover:text-red-600" onClick={() => handleDeleteTopic(topic.topic_id)} data-testid="delete-topic-btn">
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-1.5 mb-3">
                        {topic.keywords.map((kw) => (
                          <Badge key={kw} variant="outline" className="text-[10px] bg-violet-50 text-violet-600 border-violet-200">{kw}</Badge>
                        ))}
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {topic.platforms.map(p => {
                          const cfg = PLATFORM_CONFIG[p];
                          return cfg ? <Badge key={p} className={`text-[10px] ${cfg.color} text-white border-transparent`}>{cfg.label}</Badge> : null;
                        })}
                      </div>
                      <Button size="sm" variant="ghost" className="mt-3 text-xs w-full" onClick={() => { setSelectedTopic(topic.topic_id); setTab('mentions'); }} data-testid="view-mentions-btn">
                        View Mentions <ChevronRight className="h-3 w-3 ml-1" />
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>

        <CreateTopicDialog open={showCreateDialog} onClose={() => setShowCreateDialog(false)} onCreated={(t) => { setTopics(prev => [t, ...prev]); fetchData(); }} />
      </div>
    </DashboardLayout>
  );
}
